'use client'
import { useTranslation } from 'react-i18next'

import { clsx } from 'clsx'

import { BffInterfacesBasket } from '@dfds-pax/booking-experience-bff'
import { getVehicleResourceKey } from '@dfds-pax/utils'

import { useVehicleDimensions, type VehicleDimensions } from '@/hooks/useVehicleDimensions'

interface Props {
  vehicle: BffInterfacesBasket.Vehicle
  dimensionsByCode?: Record<string, VehicleDimensions>
}

export const VehicleInformation = ({ dimensionsByCode, vehicle }: Props) => {
  const { t } = useTranslation()
  const loadedVehicleDimensions = useVehicleDimensions(dimensionsByCode ? undefined : vehicle.vehicleTypeCode)
  const loadedTrailerDimensions = useVehicleDimensions(dimensionsByCode ? undefined : vehicle.trailerTypeCode)
  const vehicleDimensions = dimensionsByCode
    ? { dimensions: dimensionsByCode[vehicle.vehicleTypeCode], loading: false }
    : loadedVehicleDimensions
  const trailerDimensions = dimensionsByCode
    ? { dimensions: dimensionsByCode[vehicle.trailerTypeCode ?? ''], loading: false }
    : loadedTrailerDimensions
  return vehicle.registrationNumber ? (
    <div className="gap-semantic-global-2xs flex flex-col lg:items-start" data-e2e="vehicleSummaryCheckout">
      {vehicle.vehicleTypeCode ? (
        <>
          <label className="typography-body-md-medium text-semantic-global-text-default text-left">{t('VEHICLE')}</label>
          <div className="gap-semantic-global-2xs flex flex-col" data-e2e="vehicleSummaryCheckout">
            <div className="gap-semantic-global-2xs flex flex-row items-center">
              <Vehicle
                dimDown={true}
                height={vehicleDimensions.dimensions?.height}
                length={vehicleDimensions.dimensions?.length}
                name={t(getVehicleResourceKey(vehicle.vehicleTypeCode))}
              />
            </div>
            {vehicle.registrationNumber && vehicle.registrationNumber.length > 0 ? (
              <div className="typography-body-md-regular text-semantic-global-text-default flex flex-row flex-wrap gap-1 text-left">
                <span className="whitespace-nowrap">{t('REGISTRATION-NO')}:</span>
                <span>{[vehicle?.registrationNumber?.toUpperCase(), vehicle?.make, vehicle?.model].filter(Boolean).join(', ')}</span>
              </div>
            ) : null}
            {vehicle?.driverName && vehicle?.driverName.length > 0 ? (
              <div className="typography-body-md-regular text-semantic-global-text-default flex flex-row flex-wrap gap-1 text-left">
                <span className="whitespace-nowrap">{t('DRIVER')}:</span>
                <span>{vehicle.driverName}</span>
              </div>
            ) : null}
            {vehicle.vehicleTypeCode && (!vehicle.registrationNumber || vehicle.registrationNumber.length === 0) ? (
              <span className="typography-body-md-regular text-semantic-global-text-light lg:typography-body-md-regular text-left">
                {t('MISSING-CHECKOUT-INFORMATION')}
              </span>
            ) : null}
          </div>
        </>
      ) : null}
      {vehicle.trailerTypeCode ? (
        <>
          <label className="typography-body-md-medium text-semantic-global-info-darker text-left">{t('TRAILER')}</label>
          <div className="gap-semantic-global-2xs flex flex-col" data-e2e="trailerSummaryCheckout">
            <div className="gap-semantic-global-2xs flex flex-row items-center">
              <Vehicle
                dimDown={true}
                height={trailerDimensions.dimensions?.height}
                length={trailerDimensions.dimensions?.length}
                name={t(getVehicleResourceKey(vehicle.trailerTypeCode))}
              />
            </div>
            {vehicle.trailerRegistrationNumber && vehicle.trailerRegistrationNumber.length > 0 ? (
              <div className="typography-body-md-regular lg:typography-body-md-regular text-semantic-global-text-default flex flex-row flex-wrap gap-1 text-left">
                <span className="whitespace-nowrap">{t('REGISTRATION-NO')}:</span>
                <span>
                  {[vehicle?.trailerRegistrationNumber?.toUpperCase(), vehicle?.trailerMake, vehicle?.trailerModel]
                    .filter(Boolean)
                    .join(', ')}
                </span>
              </div>
            ) : null}
            {vehicle.trailerTypeCode && (!vehicle.registrationNumber || vehicle.registrationNumber.length === 0) ? (
              <span className="typography-body-md-regular text-semantic-global-text-light text-left">
                {t('MISSING-CHECKOUT-INFORMATION')}
              </span>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  ) : null
}

interface VehicleProps {
  dimDown?: boolean
  height?: number
  length?: number
  name: string
}

const Vehicle: React.FC<VehicleProps> = ({ dimDown, height, length, name }) => {
  return (
    <div className="gap-primitive-4 typography-body-md-regular flex flex-row flex-wrap items-center gap-x-2 text-left">
      <span>{`1 x ${name}`}</span>
      {height !== undefined && length !== undefined ? (
        <span
          className={clsx(dimDown && 'text-semantic-global-text-light typography-body-md-regular')}
        >{`(<${height}m x <${length}m)`}</span>
      ) : null}
    </div>
  )
}
